/**
 * The API client. Nothing else in the app calls fetch.
 *
 * Three things are easy to get wrong at a scattered call site and are therefore
 * centralised here:
 *
 *   credentials: 'include'  — the API is on a different origin (api.trust.…),
 *     so without this the session cookie is never sent and every request looks
 *     signed-out.
 *
 *   X-Trust-Request on mutations — the CSRF defence. A cross-site form post
 *     cannot set a custom header, and anything that can is subject to preflight,
 *     which only our origin passes. Forget it at one call site and that endpoint
 *     returns 403 forever.
 *
 *   401 handling — the API answers 401 with JSON rather than redirecting,
 *     precisely so fetch cannot follow a redirect and hand a component an HTML
 *     sign-in page as if it were data. Turning that into a real redirect is this
 *     module's job.
 */

// A bare reference so bun substitutes it at build time. A `typeof process`
// guard would look safer and is worse: the guard survives bundling, `process`
// is undefined in a browser, and the base URL silently becomes '' — sending
// every call to the app's own origin to 404 with no clue why.
const BASE = process.env.PUBLIC_API_URL ?? '';
const REQUEST_HEADER = 'X-Trust-Request';

/**
 * Where a provider's sign-in flow lives: on the API host, because the app is
 * served from a service that sets no cookies and the OAuth round trip needs a
 * host that can. Points at the route that starts the flow rather than
 * /auth/login, which renders a sign-in page — the app already drew one.
 */
export const signInURL = (provider = 'google') => `${BASE}/auth/${provider}`;

/**
 * A file export. Reached by an ordinary link rather than through this client:
 * the browser should navigate and save the file, and fetching would mean
 * holding the whole document in memory to hand back to a download the browser
 * was already willing to do.
 */
export const exportURL = (format) => `${BASE}/exports/${format}`;

/**
 * An artifact's stored response, exactly as the provider sent it. A link for
 * the same reason as exports: a person opens it to read or save the file, and
 * it should reach them as the provider's own answer, not re-rendered by us.
 */
export const evidenceContentURL = (artifactID) => `${BASE}/evidence/artifacts/${artifactID}/content`;

/**
 * An access review as Markdown: the file handed to an auditor, as opposed to
 * the page, which is the same review rendered for reading here. A link for the
 * same reason as the exports above.
 */
export const accessReviewDocumentURL = (reviewID) => `${BASE}/access-reviews/${reviewID}/document`;

/**
 * The customers' sign-in, on the API host for the same reason as the admin's:
 * the cookie belongs there. Through Ory, with their Hyphen account.
 */
export const portalSignInURL = () => `${BASE}/portal/auth/login`;

/**
 * A portal document. A link, like the exports: the browser navigates and saves
 * the file, and the API checks the grant and records the download as it does.
 */
export const portalDownloadURL = (documentID) => `${BASE}/portal/documents/${documentID}/download`;

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function request(method, path, body, { admin = true } = {}) {
  const headers = {};
  const options = { method, credentials: 'include', headers };

  if (method !== 'GET' && method !== 'HEAD') headers[REQUEST_HEADER] = '1';
  if (body instanceof FormData) {
    // No Content-Type: the browser sets multipart's, boundary and all.
    options.body = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  const response = await fetch(BASE + path, options);

  // Only the admin is sent to the admin's sign-in. A customer's 401 is theirs
  // to handle: sending a customer to Google sign-in for Hyphen staff would be
  // the wrong door entirely.
  if (response.status === 401 && admin) {
    // A session that lapsed mid-use. Straight to the flow rather than to a
    // sign-in page: they asked for a page, not to sign in, and if their Google
    // session is still good they will not be prompted at all.
    location.assign(signInURL());
    throw new ApiError(401, 'unauthenticated', 'sign-in required');
  }

  if (response.status === 204) return null;

  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // A non-JSON body from a JSON API means something between us and the
      // handler answered — a proxy, a load balancer, an error page. Say that
      // rather than surfacing a parse error from deep in a component.
      throw new ApiError(response.status, 'unexpected_response',
        `expected JSON from ${path}, got ${response.status}`);
    }
  }

  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiError(response.status, error.code ?? 'unknown', error.message ?? response.statusText);
  }
  return payload;
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  patch: (path, body) => request('PATCH', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path) => request('DELETE', path),
  // A file, as multipart form data in a field named "file".
  upload: (path, file) => {
    const form = new FormData();
    form.append('file', file);
    return request('POST', path, form);
  },
};

/**
 * The client the public portal uses. Identical but for 401, which it reports
 * rather than turning into the admin's sign-in.
 */
export const portalApi = {
  get: (path) => request('GET', path, undefined, { admin: false }),
  post: (path, body) => request('POST', path, body, { admin: false }),
  delete: (path) => request('DELETE', path, undefined, { admin: false }),
};
